/**
 * Loading, error and empty — the three states every data-driven view has, and
 * the three most often left to each screen to reinvent.
 *
 * Consistency here is not cosmetic. When each screen invents its own empty
 * state, some forget one entirely and render a blank panel that looks broken;
 * others show a spinner that never resolves because the error case was never
 * handled. Making all three explicit and shared means a screen has to *choose*
 * not to handle one, rather than forget.
 *
 * Every one of them is announced to assistive technology. A spinner nobody
 * announces is a silent page to a screen-reader user.
 */

import type { ReactNode } from "react";

import "./states.css";

/** A view that is loading for the first time — nothing to show yet. */
export function LoadingState({ label }: { label: string }) {
  return (
    <div className="hardauto-state" data-testid="loading-state">
      {/*
        `role="status"` with `aria-live="polite"` announces the label without
        interrupting whatever the user is reading. A bare spinner is invisible
        to a screen reader, which is the accessibility failure people notice
        last and users notice first.
      */}
      <div role="status" aria-live="polite" className="hardauto-state__body">
        <span className="hardauto-spinner" aria-hidden="true" />
        <p className="hardauto-state__title">{label}</p>
      </div>
    </div>
  );
}

/**
 * A view that failed.
 *
 * `onRetry` is optional but almost always worth passing: the most common cause
 * of a failed request is a transient network problem, and a screen that offers
 * no way to try again forces a full reload.
 *
 * **A retry must come with its label**, which the type enforces rather than
 * defaulting. It defaulted to "Try again" before, and that English word was
 * on a French portal in front of a customer: `core-ui` cannot translate — by
 * contract it knows no namespace — so the only safe default is none, and the
 * caller that has a translator passes one. Making it a compile error is what
 * turns a language leak into something CI catches.
 */
export type ErrorStateProps = {
  title: string;
  description?: string;
} & ({ onRetry: () => void; retryLabel: string } | { onRetry?: undefined; retryLabel?: undefined });

export function ErrorState({ title, description, onRetry, retryLabel }: ErrorStateProps) {
  return (
    <div className="hardauto-state" data-testid="error-state">
      {/*
        `role="alert"` interrupts, which is right here and wrong for loading:
        a failure is something the user must know about now.
      */}
      <div role="alert" className="hardauto-state__body">
        <span className="hardauto-state__icon hardauto-state__icon--danger" aria-hidden="true">
          !
        </span>
        <p className="hardauto-state__title">{title}</p>
        {description ? <p className="hardauto-state__description">{description}</p> : null}
        {onRetry ? (
          <button type="button" className="hardauto-state__action" onClick={onRetry}>
            {retryLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A view that succeeded and has nothing to show.
 *
 * Distinct from loading and from error on purpose: "there is nothing here yet"
 * and "something went wrong" look identical to a user if both render a blank
 * panel, and the right response to each is completely different.
 *
 * `action` is where a first-run empty state earns its keep — "no invoices yet"
 * beside a "Create one" button is a better first screen than a dashboard of
 * zeroes.
 */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="hardauto-state" data-testid="empty-state">
      <div className="hardauto-state__body">
        <span className="hardauto-state__icon" aria-hidden="true">
          &#9744;
        </span>
        <p className="hardauto-state__title">{title}</p>
        {description ? <p className="hardauto-state__description">{description}</p> : null}
        {action}
      </div>
    </div>
  );
}
